import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ChefCateringManagement = ({ userId }) => {
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [acceptsCatering, setAcceptsCatering] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null); // 'basic', 'premium', or 'deluxe'
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadCateringProfile();
    loadCateringBookings();
  }, [userId]);

  const loadCateringProfile = async () => {
    try {
      const response = await axios.get(`/catering/chef/profile/${userId}`);
      setProfile(response.data);
      setAcceptsCatering(response.data.accepts_catering || false);
    } catch (error) {
      console.error('Failed to load catering profile:', error);
    }
  };

  const loadCateringBookings = async () => {
    try {
      const response = await axios.get(`/catering/bookings/chef/${userId}`);
      setBookings(response.data.bookings || []);
    } catch (error) {
      console.error('Failed to load catering bookings:', error);
    }
  };

  const toggleCateringAcceptance = async () => {
    setLoading(true);
    try {
      await axios.post('/catering/chef/profile', {
        accepts_catering: !acceptsCatering,
        basic_menu: profile?.basic_menu || [],
        premium_menu: profile?.premium_menu || [],
        deluxe_menu: profile?.deluxe_menu || []
      });
      setAcceptsCatering(!acceptsCatering);
      alert(acceptsCatering ? 'Catering opt-out successful' : 'Catering opt-in successful!');
      loadCateringProfile();
    } catch (error) {
      console.error('Failed to update catering acceptance:', error);
      alert('Failed to update catering settings');
    } finally {
      setLoading(false);
    }
  };

  const startEditingMenu = (tier) => {
    const menuKey = `${tier}_menu`;
    setEditingMenu(tier);
    setMenuItems(profile?.[menuKey] || []);
  };

  const addMenuItem = () => {
    const courseCount = editingMenu === 'basic' ? 3 : editingMenu === 'premium' ? 4 : 5;
    setMenuItems([...menuItems, {
      name: '',
      description: '',
      course_number: menuItems.length + 1,
      dietary_options: []
    }]);
  };

  const updateMenuItem = (index, field, value) => {
    const updated = [...menuItems];
    updated[index][field] = value;
    setMenuItems(updated);
  };

  const removeMenuItem = (index) => {
    setMenuItems(menuItems.filter((_, i) => i !== index));
  };

  const saveMenu = async () => {
    setLoading(true);
    try {
      const menuKey = `${editingMenu}_menu`;
      await axios.post('/catering/chef/profile', {
        accepts_catering: acceptsCatering,
        [menuKey]: menuItems,
        basic_menu: editingMenu === 'basic' ? menuItems : (profile?.basic_menu || []),
        premium_menu: editingMenu === 'premium' ? menuItems : (profile?.premium_menu || []),
        deluxe_menu: editingMenu === 'deluxe' ? menuItems : (profile?.deluxe_menu || [])
      });
      alert('Menu saved successfully!');
      setEditingMenu(null);
      loadCateringProfile();
    } catch (error) {
      console.error('Failed to save menu:', error);
      alert('Failed to save menu');
    } finally {
      setLoading(false);
    }
  };

  const getTierInfo = (tier) => {
    const info = {
      basic: { price: 75, courses: 3, color: 'blue' },
      premium: { price: 100, courses: 4, color: 'purple' },
      deluxe: { price: 150, courses: 5, color: 'orange' }
    };
    return info[tier];
  };

  return (
    <div className="space-y-6">
      {/* Header & Opt-in */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">🍽️ Catering Services</h2>
            <p className="text-gray-600 mt-1">Manage large group bookings (10-100 guests)</p>
          </div>
          <button
            onClick={toggleCateringAcceptance}
            disabled={loading}
            className={`px-6 py-3 rounded-lg font-bold text-lg ${
              acceptsCatering
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-green-600 hover:bg-green-700 text-white'
            } disabled:bg-gray-400`}
          >
            {acceptsCatering ? '❌ Opt Out of Catering' : '✅ Accept Catering Orders'}
          </button>
        </div>

        {acceptsCatering && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-900 font-semibold">
              ✅ You are currently accepting catering orders
            </p>
            <p className="text-sm text-green-700 mt-1">
              Admins can assign catering bookings to you for groups of 10-100 guests
            </p>
          </div>
        )}
      </div>

      {/* Catering Pricing Info */}
      {acceptsCatering && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-bold mb-4">💰 Catering Pricing Structure</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
              <h4 className="font-bold text-blue-900">Basic - $75/plate</h4>
              <p className="text-sm text-blue-700">3-course meal</p>
            </div>
            <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
              <h4 className="font-bold text-purple-900">Premium - $100/plate</h4>
              <p className="text-sm text-purple-700">4-course meal</p>
            </div>
            <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4">
              <h4 className="font-bold text-orange-900">Deluxe - $150/plate</h4>
              <p className="text-sm text-orange-700">5-course meal</p>
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-3">
            Plus: $300 setup fee + $125 delivery fee • Operating fee: 20% of subtotal
          </p>
        </div>
      )}

      {/* Catering Menus */}
      {acceptsCatering && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-bold mb-4">📋 Catering Menus</h3>
          <div className="grid grid-cols-3 gap-4">
            {['basic', 'premium', 'deluxe'].map(tier => {
              const info = getTierInfo(tier);
              const menuKey = `${tier}_menu`;
              const menu = profile?.[menuKey] || [];
              
              return (
                <div key={tier} className={`bg-${info.color}-50 border-2 border-${info.color}-200 rounded-lg p-4`}>
                  <h4 className="font-bold text-lg capitalize mb-2">{tier}</h4>
                  <p className="text-sm mb-3">${info.price}/plate • {info.courses} courses</p>
                  
                  {menu.length > 0 ? (
                    <div className="space-y-2 mb-3">
                      {menu.map((item, idx) => (
                        <div key={idx} className="text-sm">
                          <p className="font-semibold">Course {item.course_number}: {item.name}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-600 mb-3">No menu set</p>
                  )}
                  
                  <button
                    onClick={() => startEditingMenu(tier)}
                    className={`w-full bg-${info.color}-600 hover:bg-${info.color}-700 text-white py-2 rounded-lg font-semibold text-sm`}
                  >
                    {menu.length > 0 ? 'Edit Menu' : 'Create Menu'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Menu Editor Modal */}
      {editingMenu && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold capitalize">{editingMenu} Menu Editor</h3>
              <button
                onClick={() => setEditingMenu(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              {getTierInfo(editingMenu).courses} courses required
            </p>

            <div className="space-y-4">
              {menuItems.map((item, index) => (
                <div key={index} className="border border-gray-300 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="font-semibold">Course {index + 1}</label>
                    <button
                      onClick={() => removeMenuItem(index)}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                  
                  <input
                    type="text"
                    placeholder="Dish name"
                    value={item.name}
                    onChange={(e) => updateMenuItem(index, 'name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2"
                  />
                  
                  <textarea
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) => updateMenuItem(index, 'description', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    rows="2"
                  />
                </div>
              ))}

              <button
                onClick={addMenuItem}
                className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 py-2 rounded-lg font-semibold"
              >
                + Add Course
              </button>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={saveMenu}
                disabled={loading}
                className="flex-1 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white py-3 rounded-lg font-bold"
              >
                {loading ? 'Saving...' : 'Save Menu'}
              </button>
              <button
                onClick={() => setEditingMenu(null)}
                className="px-6 bg-gray-300 hover:bg-gray-400 text-gray-700 py-3 rounded-lg font-semibold"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Catering Bookings */}
      {acceptsCatering && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-bold mb-4">📅 Your Catering Bookings ({bookings.length})</h3>
          {bookings.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No catering bookings yet</p>
          ) : (
            <div className="space-y-4">
              {bookings.map(booking => (
                <div key={booking.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-bold text-lg">{booking.customer_name}</h4>
                      <p className="text-sm text-gray-600">{booking.customer_email}</p>
                      <p className="text-sm text-gray-600">
                        {new Date(booking.date).toLocaleDateString()} at {new Date(booking.date).toLocaleTimeString()}
                      </p>
                      <p className="text-sm text-gray-600">
                        {booking.guest_count} guests • {booking.tier.charAt(0).toUpperCase() + booking.tier.slice(1)} tier (${booking.price_per_plate}/plate)
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        📍 {booking.service_address}, {booking.service_city}, {booking.service_state}
                      </p>
                      {booking.special_requests && (
                        <p className="text-sm text-gray-600 mt-1">
                          <strong>Requests:</strong> {booking.special_requests}
                        </p>
                      )}
                      <p className="text-sm font-semibold text-green-600 mt-2">
                        Your earnings: ${booking.plates_total + booking.setup_fee}
                      </p>
                      <p className="text-xs text-gray-500">
                        (Plates: ${booking.plates_total} + Setup: ${booking.setup_fee})
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                      booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {booking.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChefCateringManagement;
